import React, { useRef, useState } from 'react';
import { Button } from '../../ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '../../ui/avatar';
import { ArrowLeft, Camera, Upload, User } from 'lucide-react';

interface AvatarStepProps {
  avatar: File | null;
  onAvatarChange: (avatar: File | null) => void;
  onNext: () => void;
  onBack: () => void;
  existingPhotoURL?: string;
}

export function AvatarStep({ avatar, onAvatarChange, onNext, onBack, existingPhotoURL }: AvatarStepProps) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(existingPhotoURL || null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      onAvatarChange(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onAvatarChange(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setPreviewUrl(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = () => {
    setIsDragging(false);
  };

  return (
    <div className="space-y-8">
      <div className="text-center space-y-3">
        <h2 className="text-3xl md:text-4xl font-bold text-foreground tracking-tight">Add a profile photo</h2>
        <p className="text-lg text-muted-foreground">Help your team recognize you</p>
      </div>

      <div className="flex flex-col items-center gap-6">
        <div
          className={`relative cursor-pointer transition-all duration-200 ${isDragging ? 'scale-105' : ''}`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onClick={() => fileInputRef.current?.click()}
        >
          <Avatar className="w-40 h-40 border-4 border-dashed border-prosigliere-blue/30 hover:border-prosigliere-blue transition-colors">
            {previewUrl ? (
              <AvatarImage src={previewUrl} alt="Profile" />
            ) : (
              <AvatarFallback className="bg-secondary">
                <User className="w-16 h-16 text-muted-foreground" />
              </AvatarFallback>
            )}
          </Avatar>
          <div className="absolute -bottom-2 -right-2 bg-prosigliere-blue text-white rounded-full p-2 shadow-lg">
            <Camera className="w-5 h-5" />
          </div>
        </div>

        <input ref={fileInputRef} type="file" accept="image/*" onChange={handleFileChange} className="hidden" />

        <button
          onClick={() => fileInputRef.current?.click()}
          className="flex items-center gap-2 text-prosigliere-blue hover:text-prosigliere-blue/80 font-medium transition-colors"
        >
          <Upload className="w-4 h-4" />
          Upload a photo
        </button>

        <p className="text-sm text-muted-foreground text-center">Drag and drop or click to upload</p>
      </div>

      <div className="flex items-center justify-between pt-8">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ArrowLeft className="w-4 h-4" />
          Back
        </Button>
        <Button onClick={onNext} className="rounded-full px-8">
          {avatar ? 'Confirm' : 'Next'}
        </Button>
      </div>
    </div>
  );
}
